import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { DynamicTableStateless } from '@atlaskit/dynamic-table';
import { Stack, Text, xcss, Box, Inline } from '@atlaskit/primitives';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { EmptySearchVector } from '../../displays/assets/empty-search-vector';

import { SORT_DIRECTION, PAGINATION_DIRECTION } from './constants';
import { FastshiftTableFilterProvider } from './fastshift-table-filter-context';
import { FastshiftTablePagination } from './fastshift-table-pagination';
import { FastshiftTableSearch } from './fastshift-table-search';
import { FastshiftTableWrapper } from './fastshift-table-wrapper';
import { useFastshiftTableControls, useFastshiftTableAnalytics } from './hooks';
import { messages } from './messages';
import { FastshiftTableProps, SortBy } from './types';

const emptyTableContainerStyles = xcss({
	height: '200px',
	width: '100%',
	padding: 'space.400',
});

const emptyTableIconContainer = xcss({
	paddingBlock: 'space.100',
});

const FastshiftTableToolbar = ({
	enableSearch,
	id,
	searchAriaLabel,
	search,
	testId,
	toolbarContent,
	onFilterClear,
	summaryText,
}: {
	enableSearch: boolean;
	id: string;
	searchAriaLabel?: string;
	search: React.ComponentProps<typeof FastshiftTableSearch>['search'];
	testId: string;
	toolbarContent?: React.ReactNode;
	onFilterClear?: () => void;
	summaryText?: string;
}) => {
	const hasToolbarRow = enableSearch || Boolean(toolbarContent);
	const hasToolbarSection = hasToolbarRow || Boolean(summaryText);

	if (!hasToolbarSection) {
		return null;
	}

	return (
		<Stack space="space.200">
			{hasToolbarRow && (
				<Inline space="space.100" testId={`${testId}-toolbar`} shouldWrap>
					{enableSearch && (
						<FastshiftTableSearch
							id={`${id}-search`}
							ariaLabel={searchAriaLabel}
							search={search}
							testId={`${testId}-search`}
						/>
					)}
					{toolbarContent && (
						<FastshiftTableFilterProvider onFilterClear={onFilterClear}>
							{toolbarContent}
						</FastshiftTableFilterProvider>
					)}
				</Inline>
			)}
			{summaryText && <Text size="medium">{summaryText}</Text>}
		</Stack>
	);
};

export const FastshiftTable: React.FC<FastshiftTableProps> = ({
	tableCaption,
	enableSearch = false,
	searchAriaLabel,
	summaryText,
	toolbarContent,
	toolbarSpacing = 'space.200',
	isTableLoading,
	isTableError,
	errorComponent,
	head,
	rows,
	controls,
	id = 'fastshift-table',
	testId = 'fastshift-table',
	showToolbarOnError = false,
	onFilterClear,
}) => {
	const intl = useIntl();
	const { pagination, sorting, search } = useFastshiftTableControls({
		...controls,
		enableSearch,
	});

	const { sendSearchUIEvent, sendNavigationUIEvent, sendPageSizeChangeUIEvent, sendSortUIEvent } =
		useFastshiftTableAnalytics();

	// Enhanced sort handler that triggers analytics
	const handleSort = useCallback(
		(key: SortBy) => {
			const nextOrder =
				sorting.sortBy === key && sorting.sortDirection === SORT_DIRECTION.ASC
					? SORT_DIRECTION.DESC
					: SORT_DIRECTION.ASC;
			sorting.onSort(key, nextOrder);
			sendSortUIEvent(key, nextOrder);
		},
		[sorting, sendSortUIEvent],
	);

	const handleSearchTermChange = useCallback(
		(searchTerm: string) => {
			if (!enableSearch) return;
			search?.onSetSearchTerm(searchTerm);
			sendSearchUIEvent();
		},
		[enableSearch, search, sendSearchUIEvent],
	);

	const enhancedSearch = {
		...search,
		onSetSearchTerm: handleSearchTermChange,
	};

	const handleClickNext = useCallback(() => {
		pagination.onClickNext();
		sendNavigationUIEvent(PAGINATION_DIRECTION.NEXT, pagination.totalPages);
	}, [pagination, sendNavigationUIEvent]);

	const handleClickPrevious = useCallback(() => {
		pagination.onClickPrevious();
		sendNavigationUIEvent(PAGINATION_DIRECTION.PREVIOUS, pagination.totalPages);
	}, [pagination, sendNavigationUIEvent]);

	const handleSizeChange = useCallback(
		(size: number) => {
			pagination.onSizeChange(size);
			sendPageSizeChangeUIEvent(size);
		},
		[pagination, sendPageSizeChangeUIEvent],
	);

	if (isTableError && !showToolbarOnError) {
		return errorComponent;
	}

	return (
		<Stack space={toolbarSpacing}>
			<FastshiftTableToolbar
				enableSearch={enableSearch}
				id={id}
				searchAriaLabel={searchAriaLabel}
				search={enhancedSearch}
				testId={testId}
				toolbarContent={toolbarContent}
				onFilterClear={onFilterClear}
				summaryText={summaryText}
			/>

			{isTableError ? (
				errorComponent
			) : (
				<FastshiftTableWrapper id={`${id}-wrapper`} testId={`${testId}-wrapper`}>
					<DynamicTableStateless
						caption={tableCaption ? <VisuallyHidden>{tableCaption}</VisuallyHidden> : undefined}
						testId={`${testId}-table`}
						head={head}
						rows={rows}
						isLoading={isTableLoading}
						isFixedSize
						onSort={(data) => handleSort(data.key as SortBy)}
						sortOrder={sorting.sortDirection ?? SORT_DIRECTION.ASC}
						sortKey={sorting.sortBy}
						emptyView={
							<Stack
								alignBlock="center"
								alignInline="center"
								xcss={emptyTableContainerStyles}
								space="space.300"
							>
								<Box xcss={emptyTableIconContainer}>
									<EmptySearchVector />
								</Box>
								<Text>{intl.formatMessage(messages.emptyTableMessage)}</Text>
							</Stack>
						}
					/>

					<FastshiftTablePagination
						pagination={{
							...pagination,
							onClickNext: handleClickNext,
							onClickPrevious: handleClickPrevious,
							onSizeChange: handleSizeChange,
						}}
					/>
				</FastshiftTableWrapper>
			)}
		</Stack>
	);
};
